// /components/NewWorkflow/shared/SchemaViewer.js

import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Type, Hash, ToggleRight, Braces, Brackets, Link2, PlusCircle, ChevronRight } from 'lucide-react';

const cn = (...inputs) => twMerge(clsx(inputs));

const DATA_TYPES = {
    string: { label: 'String', icon: <Type size={14} /> },
    number: { label: 'Number', icon: <Hash size={14} /> },
    boolean: { label: 'Boolean', icon: <ToggleRight size={14} /> },
    object: { label: 'Object', icon: <Braces size={14} /> },
    array: { label: 'Array', icon: <Brackets size={14} /> },
};

const Field = ({ field, path, isOutput, onFieldClick, onPassThroughClick, selectedPath, registerRef = () => { }, mappings = {}, isValidField = () => true, isInsideArray = false }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const dataType = (field.dataType || 'string').toLowerCase();
    const arrayItemType = (field.arrayItemType || 'string').toLowerCase();
    const info = DATA_TYPES[dataType] || DATA_TYPES.string;

    const isSelected = selectedPath === path;

    // const isClickable = !!onFieldClick;
    // const isDimmed = onFieldClick && !isValidField(field); // Check if field should be dimmed
    // ⭐ 2. Determine if the field is selectable based on the new flag and existing validation.
    const isClickableContext = !!onFieldClick;
    const isSelectable = isClickableContext && !isInsideArray && isValidField(field);


    const isMapped = useMemo(() => {
        if (isOutput) {
            return !!mappings[field.id];
        }
        return Object.values(mappings).some(p => p.join('.') === path);
    }, [isOutput, mappings, field.id, path]);

    const mappedFromPath = isOutput ? (mappings[field.id] || []).join('.') : null;
    const isNestable = (dataType === 'object' || (dataType === 'array' && arrayItemType === 'object')) && (field.children || []).length > 0;

    const handlePassThrough = (e) => {
        e.stopPropagation();
        if (onPassThroughClick) {
            onPassThroughClick(field, path);
        }
    };

    return (
        <div className="pl-4 relative before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-slate-700">
            <div className="relative">
                <div
                    // onClick={() => isClickable && !isDimmed && onFieldClick(path, field)}
                    onClick={() => isSelectable && onFieldClick(path, field)}
                    className={cn(
                        "group flex items-center justify-between pl-2 pr-1 py-1.5 rounded-md transition-all duration-150 border",
                        "before:absolute before:left-[-17px] before:top-1/2 before:w-4 before:h-px before:bg-slate-700",
                        // isClickable && "cursor-pointer",
                        isSelected ? "bg-indigo-600/30 border-indigo-500" : "bg-slate-900/50 border-transparent hover:bg-slate-700/50",
                        isMapped && "bg-teal-600/20 border-teal-500/30",
                        // isClickable && !isDimmed && "cursor-pointer",
                        // isClickable && isDimmed && "opacity-40 cursor-not-allowed", // Dim invalid fields
                        // ⭐ 3. Update styling logic to dim and disable non-selectable fields.
                        isClickableContext && isSelectable && "cursor-pointer hover:bg-slate-700/50",
                        isClickableContext && !isSelectable && "opacity-50 cursor-not-allowed",
                    )}
                >
                    <div ref={el => registerRef(`handle-${isOutput ? 'target' : 'source'}-${path}`, el)} className="absolute left-0 top-1/2 -translate-x-[5px] w-2.5 h-2.5 rounded-full bg-slate-600 group-hover:bg-slate-400 transition-colors z-10" />

                    <div className="flex items-center gap-2 flex-grow min-w-0">
                        {isNestable ? <ChevronRight size={16} className={cn("text-slate-500 transition-transform flex-shrink-0 cursor-pointer", isExpanded && "rotate-90")} onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} /> : <div className="w-4 flex-shrink-0" />}
                        {isMapped && isOutput && <Link2 size={14} className="text-teal-400 flex-shrink-0" />}
                        <span className="font-mono text-sm text-white truncate" title={field.fieldName}>{field.fieldName || <i className="text-slate-500">untitled</i>}</span>
                        <span className="flex items-center gap-1.5 text-xs text-slate-400 flex-shrink-0">{info.icon} {info.label}</span>
                    </div>

                    <div className="flex items-center flex-shrink-0">
                        {/* --- THIS IS THE RESTORED PASS-THROUGH BUTTON --- */}
                        {!isOutput && !isMapped && onPassThroughClick && (
                            <button onClick={handlePassThrough} className="mr-2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-indigo-400 transition-all" title={`Add "${field.fieldName}" to output`}>
                                <PlusCircle size={16} />
                            </button>
                        )}
                        <div ref={el => registerRef(`handle-${isOutput ? 'source' : 'target'}-${path}`, el)} className="relative right-[-13px] w-2.5 h-2.5 rounded-full bg-slate-600 group-hover:bg-slate-400 transition-colors z-10" />
                    </div>
                </div>

                {isMapped && isOutput && mappedFromPath && (<div className="mt-1 text-xs pl-8 text-indigo-300 font-mono truncate"><span className="text-slate-500">maps from → </span>{mappedFromPath}</div>)}
            </div>

            {isNestable && isExpanded && (
                <div className="mt-2 space-y-2">
                    {field.children.map((child) => (
                        <Field key={child.id} field={child} path={`${path}.${child.fieldName}`}
                            isInsideArray={isInsideArray || dataType === 'array'}
                            // ⭐ THE FIX: Ensure `isValidField` is passed down to child components
                            {...{ onFieldClick, onPassThroughClick, selectedPath, registerRef, isOutput, mappings, isValidField }} />
                    ))}
                </div>
            )}
        </div>
    );
};

const SchemaViewer = ({ rootFields, ...props }) => {
    if (!rootFields || rootFields.length === 0) {
        return <div className="text-slate-500 text-sm p-4 text-center rounded-lg bg-slate-800">No Schema Available</div>;
    }

    return (
        <div className="space-y-3 p-2 rounded-lg bg-slate-800">
            {rootFields.map(rootField => (
                <Field
                    key={rootField.id}
                    field={rootField}
                    path={rootField.id}
                    {...props}
                />
            ))}
        </div>
    );
};

export default SchemaViewer;